/**
 * Mike's Liquors — Keg Accessories Controller (Phase 1; v2 UI 2026-09-26)
 *
 * v2 (panel redesign): the cart request is unchanged. Added: a persistent
 * "Added ✓" state that re-arms only when the shopper changes something (stops
 * accidental double orders), a real itemCount on cart:update (header bubble),
 * fly-to-cart, a screen-reader status line, and routing of Horizon's floating
 * "Add to cart" bar (dead on keg pages) to this panel.
 *
 * Works with keg-accessories.liquid and the existing keg-deposit v2.1 module.
 * On a keg product page:
 *   1. Reads the customer's selections from the accessory panel.
 *   2. Computes a running add-on total (live).
 *   3. On "Add to Cart" click, posts a single /cart/add.js call with the keg
 *      + every selected add-on in items[]. Each add-on line carries a hidden
 *      "_For Keg" property so staff can match add-ons to parent kegs on
 *      multi-keg orders.
 *   4. Writes the "Returning Empty Kegs" count to cart attributes so the
 *      existing cart-page return panel (v2.1) picks it up.
 *
 * keg-deposit v2.1 will auto-add the $50 deposit when it sees the keg land
 * in the cart via the fetch wrapper — no action needed here.
 *
 * Variant ids come from window.MIKES_ADDONS (set in theme.liquid). Handles
 * that are missing are skipped gracefully with a console warning.
 */

(function mikesKegAccessories() {
  'use strict';

  var root = document.getElementById('mikes-keg-accessories');
  if (!root) return;
  if (!window.MIKES_ADDONS) {
    console.warn('[Keg Accessories] window.MIKES_ADDONS is missing — set it in theme.liquid.');
    return;
  }

  var kegVariantId = parseInt(root.dataset.kegVariant, 10);
  // Health-Ade / Stumptown kegs ship one-way (non-refundable) — no keg deposit.
  // Set in keg-accessories.liquid from NO_DEPOSIT_KEG_VENDORS.
  var kegNoDeposit = root.dataset.kegNoDeposit === 'true';
  var kegTitle = root.dataset.kegTitle || 'Keg';
  var couplerType = root.dataset.couplerType || 'Type D';
  var couplerHandleKey = couplerType.toLowerCase().replace(/[^a-z0-9]+/g, '-'); // "Type D" -> "type-d"

  // ── Gas picker wiring ─────────────────────────────────────────────
  // Each .mka-gas-picker (one per branch that offers gas: kegerator, jockey_box)
  // has three gas-type radios (none/co2/n2), a per-type size select, and a
  // shared qty input. Show the matching size + qty when a gas is picked,
  // hide them when "none". Variant IDs and prices are embedded in the
  // Liquid-rendered <option> tags, so we don't need MIKES_ADDONS for gas.
  root.querySelectorAll('.mka-gas-picker').forEach(function (picker) {
    var typeRadios = picker.querySelectorAll('.mka-gas-type');
    function updateVisibility() {
      var checked = picker.querySelector('.mka-gas-type:checked');
      var val = checked ? checked.value : 'none';
      picker.querySelectorAll('.mka-gas-size').forEach(function (el) {
        el.style.display = (el.getAttribute('data-gas') === val) ? '' : 'none';
      });
      var qtyDiv = picker.querySelector('.mka-gas-qty');
      if (qtyDiv) qtyDiv.style.display = (val === 'none') ? 'none' : '';
      // Deposit note for the chosen gas (nitrogen's deposit depends on size).
      picker.querySelectorAll('[data-gas-note]').forEach(function (el) {
        el.style.display = (el.getAttribute('data-gas-note') === val) ? '' : 'none';
      });
    }
    typeRadios.forEach(function (r) { r.addEventListener('change', updateVisibility); });
    updateVisibility();
  });

  /**
   * Read the gas-refill selection for a branch. Returns null if no gas is
   * selected. Shape matches buildAddons output so the caller can push into
   * lines[] directly. Each refill auto-triggers the $100 tank deposit via
   * deposits.js as soon as it lands in the cart — we don't add the deposit
   * here.
   */
  function readGasPicker(branch) {
    var picker = root.querySelector('.mka-gas-picker[data-branch="' + branch + '"]');
    if (!picker) return null;
    var typeInput = picker.querySelector('.mka-gas-type:checked');
    var type = typeInput ? typeInput.value : 'none';
    if (type === 'none') return null;
    var sizeDiv = picker.querySelector('.mka-gas-size[data-gas="' + type + '"]');
    if (!sizeDiv) return null;
    var sel = sizeDiv.querySelector('.mka-gas-variant');
    if (!sel) return null;
    var variantId = parseInt(sel.value, 10);
    if (isNaN(variantId)) return null;
    var unitPrice = parseFloat(
      sel.options[sel.selectedIndex] && sel.options[sel.selectedIndex].getAttribute('data-price')
    ) || 0;
    var qtyInput = picker.querySelector('.mka-gas-count');
    var qty = gasQty(qtyInput);
    var sizeLabel = sel.options[sel.selectedIndex] ? sel.options[sel.selectedIndex].text.split(' — ')[0] : '';
    var gasName = type === 'co2' ? 'CO₂' : 'N₂';
    return {
      variantId: variantId,
      quantity: qty,
      price: unitPrice * qty,
      label: qty + ' × ' + gasName + ' refill (' + sizeLabel + ')',
      gasType: type, // 'co2' | 'n2' — used by atomic ATC to inject the matching tank deposit in the same /cart/add.js
    };
  }

  // Gas refill count, kept to the box's own range (1-10). A typed 15 used to
  // send 15 refills + 15 tank deposits; a cleared or 0 box silently dropped the
  // gas while it still showed as picked. The box itself is corrected to match on
  // change (see the listener below), so what's shown is what's added.
  function gasQty(input) {
    if (!input) return 1;
    var max = parseInt(input.max, 10) || 10;
    var v = parseInt(input.value, 10);
    if (isNaN(v) || v < 1) v = 1;
    if (v > max) v = max;
    return v;
  }
  root.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.classList || !t.classList.contains('mka-gas-count')) return;
    var v = String(gasQty(t));
    // Fire 'input' so the total, the stepper's +/- states and the screen-reader
    // status all follow the corrected number (this listener only hears 'change',
    // so it can't loop).
    if (t.value !== v) { t.value = v; t.dispatchEvent(new Event('input', { bubbles: true })); }
  });

  function $(sel) { return root.querySelector(sel); }
  function val(sel) { var el = $(sel); return el ? el.value : null; }
  // Clamp to [0, 999]. The HTML enforces `min="0"` already, but a hostile
  // user can bypass DOM validation by editing the input or posting /cart/add.js
  // directly — and we'd still compute subtotals / line quantities off those
  // numbers. Clamping keeps downstream math sane even under abuse.
  function num(sel) {
    var v = parseInt(val(sel) || '0', 10);
    if (isNaN(v) || v < 0) return 0;
    if (v > 999) return 999;
    return v;
  }
  function checked(sel) { var el = $(sel); return el ? el.checked : false; }
  function radio(name) { var el = root.querySelector('input[name="' + name + '"]:checked'); return el ? el.value : null; }

  // Reads the Shopify product form's quantity input so multi-keg orders
  // actually get the customer-selected count. Horizon puts the input inside
  // <quantity-input> component inside the product form; any `[name="quantity"]`
  // on the page reliably points to it. Defaults to 1 on any parse failure.
  function readKegQuantity() {
    var input = document.querySelector('product-form-component input[name="quantity"], form[action*="/cart/add"] input[name="quantity"], input[name="quantity"]');
    if (!input) return 1;
    var v = parseInt(input.value, 10);
    if (isNaN(v) || v < 1) return 1;
    if (v > 999) return 999;
    return v;
  }

  // Look up an add-on by handle. Returns { id, unitPrice } where unitPrice
  // is in DOLLARS (MIKES_ADDONS stores cents per Shopify convention).
  // Throws on missing / null so the caller's try/catch surfaces a visible
  // error to the user instead of silently skipping the line — an admin
  // unpublishing an add-on product used to make checkboxes do nothing.
  function addon(key) {
    var entry = window.MIKES_ADDONS[key];
    if (!entry || entry.id == null) {
      throw new Error('Add-on "' + key + '" is not available right now. Please contact Mike\'s Liquors.');
    }
    return { id: entry.id, unitPrice: (entry.price || 0) / 100 };
  }

  /**
   * Return an array of {variantId, quantity, price, label}. `price` is the
   * LINE total in dollars (unitPrice × quantity), read live from Shopify via
   * window.MIKES_ADDONS — never a hardcoded value. Throws if any selected
   * add-on has been unpublished.
   */
  function buildAddons() {
    var setup = radio('mka-setup') || 'none';
    var lines = [];

    function add(key, qty, label) {
      var a = addon(key);
      lines.push({ variantId: a.id, quantity: qty, price: a.unitPrice * qty, label: label });
    }

    // Cups (always available)
    if (checked('#mka-cups-enable')) {
      var pk = val('#mka-cups') === '100' ? 'cups-16oz-100pk' : 'cups-16oz-50pk';
      add(pk, 1, pk === 'cups-16oz-100pk' ? '100 cups (16oz)' : '50 cups (16oz)');
      lines[lines.length - 1].cups = true; // tag ordering only; items[] reads variantId/quantity
    }

    if (setup === 'kegerator') {
      var kegGas = readGasPicker('kegerator');
      if (kegGas) lines.push(kegGas);
      var cplr = radio('mka-coupler');
      if (cplr === 'rent' || cplr === 'buy') {
        var key = 'coupler-' + cplr + '-' + couplerHandleKey; // e.g. coupler-rent-type-d
        add(key, 1, (cplr === 'rent' ? 'Rent ' : 'Buy ') + couplerType + ' coupler');
      }
      var faucets = num('#mka-line-cleaning');
      if (faucets > 0) add('line-cleaning-service', faucets, faucets + ' × Line cleaning faucet');
    } else if (setup === 'jockey_box') {
      var size = radio('mka-jockey-size') || 'single';
      var jbKey = size === 'double' ? 'jockey-box-double-rent' : 'jockey-box-single-rent';
      add(jbKey, 1, (size === 'double' ? 'Double' : 'Single') + ' Jockey Box rental');
      var jIce = num('#mka-jockey-ice');
      if (jIce > 0) add('ice-bag', jIce, jIce + ' × ice bag');
      // No gas picker on the jockey-box branch (removed by design — it
      // confused customers). Gas is offered on the Kegerator branch only.
    } else if (setup === 'keg_tub') {
      if (checked('#mka-tub')) add('keg-tub-rental', 1, 'Keg Tub rental');
      if (checked('#mka-pump')) add(couplerHandleKey + '-pump-rental', 1, couplerType + ' pump rental');
      var tIce = num('#mka-tub-ice');
      if (tIce > 0) add('ice-bag', tIce, tIce + ' × ice bag');
    }

    return lines;
  }

  // One tag per add-on line above the total, using the same labels as
  // buildAddons. Hidden when nothing is picked (or an add-on is missing).
  function renderTags(lines) {
    var tags = $('#mka-summary');
    if (!tags) return;
    tags.textContent = '';
    // Same order as the panel: setup items first, cups (the last question) at
    // the end. Display only — buildAddons' order is the cart request order.
    var isCups = function (l) { return l.cups === true; };
    lines.filter(function (l) { return !isCups(l); }).concat(lines.filter(isCups)).forEach(function (l) {
      var li = document.createElement('li');
      li.textContent = l.label;
      tags.appendChild(li);
    });
    tags.hidden = lines.length === 0;
  }

  function updateTotal() {
    var el = $('#mka-addons-total');
    if (!el) return;
    try {
      var lines = buildAddons();
      var total = lines.reduce(function (s, l) { return s + l.price; }, 0);
      el.textContent = '$' + total.toFixed(2);
      renderTags(lines);
    } catch (err) {
      // An add-on has been unpublished — admin needs to fix it, but the
      // customer shouldn't see NaN. Show a dash and log.
      el.textContent = '—';
      renderTags([]);
      console.warn('[Keg Accessories] updateTotal:', err.message);
    }
  }

  // Recompute total on any input change
  root.addEventListener('change', updateTotal);
  root.addEventListener('input', updateTotal);
  updateTotal();

  // Add to Cart
  $('#mka-add-to-cart').addEventListener('click', function () {
    var btn = $('#mka-add-to-cart');
    var msg = $('#mka-msg');
    msg.textContent = '';
    btn.disabled = true;
    btn.textContent = 'Adding…';

    var setup = radio('mka-setup') || 'none';
    var exchange = num('#mka-exchange');
    var kegQty = readKegQuantity();

    var addons;
    try {
      addons = buildAddons();
    } catch (err) {
      // An add-on product is unpublished/deleted — refuse to proceed.
      showMsg(err.message);
      btn.textContent = 'Add to Cart';
      btn.disabled = false;
      console.error('[Keg Accessories]', err);
      return;
    }

    // Build keg's own visible properties so staff sees the choice on the order
    var kegProps = { 'Setup': setupLabel(setup) };
    if (setup === 'kegerator') {
      var cplr = radio('mka-coupler');
      if (cplr === 'have') kegProps['Coupler'] = 'Customer owns ' + couplerType;
      if (cplr === 'rent') kegProps['Coupler'] = 'Renting ' + couplerType;
      if (cplr === 'buy') kegProps['Coupler'] = 'Buying ' + couplerType;
    }

    // Honour the Shopify quantity selector. Add-on quantities are anchored
    // to the original panel inputs (e.g. "5 faucets × line cleaning" is still
    // 5, not 5 × kegQty) — customer can set those per-line.
    var items = [{ id: kegVariantId, quantity: kegQty, properties: kegProps }];
    addons.forEach(function (l) {
      items.push({
        id: l.variantId, quantity: l.quantity,
        properties: { '_For Keg': kegTitle }
      });
      // Atomic ATC: if this addon is a gas refill, include its tank deposit
      // in the SAME /cart/add.js so the cart never enters a "refill present,
      // deposit absent" state. Required for the Cart Validation Function to
      // approve the mutation; deposits.js's polling fallback would also
      // reconcile this, but eagerly bundling avoids the ~3s lag and the
      // 422 from a future-active validator.
      if (l.gasType === 'co2' && window.MIKES_CO2_DEPOSIT_VARIANT) {
        // CO₂ is size-based since 2026-10-22: the 20 lb refill (CO2-RFL-20)
        // takes the $150 CO2-20-DEP, 5/10 lb the $125 CO2-DEP. Same ids as the
        // triggerVariants in mikes-deposits.js; unknown ids get CO2-DEP.
        var co2Dep = (Number(l.variantId) === 42961750196327 && window.MIKES_CO2_DEPOSIT_VARIANT_20) ||
          window.MIKES_CO2_DEPOSIT_VARIANT;
        items.push({ id: co2Dep, quantity: l.quantity });
      } else if (l.gasType === 'n2') {
        // Nitrogen deposits are size-based since 2026-09-10 (5/10/20 lb →
        // $130/$150/$150 since 2026-10-22). Pick the deposit that matches the refill size so the
        // cart never holds the 5 lb deposit next to the 10/20 lb one. Keyed by
        // the N2-RFL-5/-10/-20 variant ids (same ids as the triggerVariants in
        // mikes-deposits.js); an unknown id falls back to the 5 lb deposit.
        var N2_DEP_BY_REFILL = {
          43771527659623: window.MIKES_N2_DEPOSIT_VARIANT,
          43771527692391: window.MIKES_N2_DEPOSIT_VARIANT_10,
          43771527725159: window.MIKES_N2_DEPOSIT_VARIANT_20
        };
        var n2Dep = N2_DEP_BY_REFILL[l.variantId] || window.MIKES_N2_DEPOSIT_VARIANT;
        if (n2Dep) items.push({ id: n2Dep, quantity: l.quantity });
      }
    });
    // Atomic ATC: include the keg deposit in the same /cart/add.js. Same
    // rationale as the gas-deposit injection above. Quantity matches kegQty
    // so multi-keg ATCs (e.g. qty=3) get 3 deposits. Skipped for one-way
    // plastic kegs (Health-Ade, Stumptown) — they take no deposit.
    if (window.MIKES_KEG_DEPOSIT_VARIANT && !kegNoDeposit) {
      items.push({ id: window.MIKES_KEG_DEPOSIT_VARIANT, quantity: kegQty });
    }
    // Card on file (2026-10-22): a business location with mikes.deposit_waived
    // pays no deposits, so each deposit above becomes its $0 "Equipment on card
    // file" line (map set in theme.liquid; mikes-deposits.js swaps the same way).
    if (window.MIKES_DEPOSIT_WAIVED === true && window.MIKES_COF_VARIANTS) {
      items.forEach(function (it) {
        var cof = window.MIKES_COF_VARIANTS[String(it.id)];
        if (cof) it.id = cof;
      });
    }

    // Lock the choices while the request is in flight so the panel can't show
    // a different setup from the one being added; a change made outside the
    // panel meanwhile (keg qty / variant / drawer) is remembered by armAgain.
    var opts = root.querySelector('.mka-opts');
    if (opts) opts.disabled = true;
    root.dataset.adding = '1';
    function unlock() {
      delete root.dataset.adding;
      if (opts && root.dataset.soldOut !== 'true') opts.disabled = false;
    }

    // Post everything in one call so the fetch wrapper sees a SELF mutation
    // (the items array contains deposit variant ids that deposits.js detects).
    var addStatus = 0;        // HTTP status of a rejected add (0 = never reached the store)
    var emptiesFailed = false;
    function readCart() {
      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) {
        if (!r.ok) {
          addStatus = r.status || 1;
          // Shopify's own sentence when it sends one (e.g. "You can only add 4 of
          // this item to your cart."); never raw parser text for an HTML/bot page.
          return r.json().catch(function () { return {}; }).then(function (j) {
            throw new Error(j.description || j.message || 'Sorry — we couldn\'t add this to your cart. Please try again.');
          });
        }
        // A success status whose body isn't JSON (a redirect to a challenge,
        // password or captive page) is NOT proof the add happened: treat it as
        // unconfirmed — re-read the cart and let the shopper check — rather than
        // locking the button on "Added ✓" over an empty cart.
        return r.json().catch(function () {
          addStatus = r.status || 1;
          throw new Error('We couldn\'t confirm this was added — please check your cart before adding again.');
        });
      })
      .then(function () {
        // Persist returning-empties count as an ADDITIVE cart attribute. A
        // previous pass would set the value, so adding a second keg with
        // exchange=0 would overwrite and silently lose the earlier count.
        // Now we fetch current, add, then write — so multi-page flows add up.
        // The keg is already in the cart by now, so a failure here must not
        // turn into "Error" + a re-armed button (a second click doubled the order).
        if (exchange <= 0) return null;
        return readCart()
          .then(function (cart) {
            if (!cart) return null;
            var current = parseInt((cart.attributes || {})['Returning Empty Kegs'] || '0', 10);
            if (isNaN(current)) current = 0;
            var next = current + exchange;
            return fetch('/cart/update.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify({ attributes: { 'Returning Empty Kegs': String(next) } })
            }).then(function (r) { return r.ok ? r.json().catch(function () { return null; }) : null; });
          })
          .catch(function () { return null; })
          .then(function (updated) { if (!updated) emptiesFailed = true; return updated; });
      })
      .then(function (updated) {
        // Real item count for the header cart bubble (Horizon's cart-icon.js
        // treats a cart:update with no itemCount as 0 and hides the bubble). The
        // empties write already returned the whole cart, so reuse it; otherwise
        // read it. A failed read just omits the count.
        if (updated && typeof updated.item_count === 'number') return updated;
        return readCart();
      })
      .then(function (cart) {
        btn.textContent = 'Added ✓';
        // Horizon's cart drawer + cart-items components listen for 'cart:update'
        // (colon, not hyphen — verified in theme/assets/events.js). With no
        // `sections` payload, <cart-items-component> falls back to fetching
        // fresh section HTML via sectionRenderer.renderSection. The deposit
        // added by keg-deposit v2.1 lands a moment later and v2.1's own
        // dispatchCartRefresh fires another cart:update to re-render.
        var updateData = { source: 'mikes-keg-accessories', sections: {} };
        if (cart && typeof cart.item_count === 'number') updateData.itemCount = cart.item_count;
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            sourceId: 'mikes-keg-accessories',
            data: updateData
          }
        }));
        // Legacy names for any custom listeners:
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart-update', { bubbles: true }));
        // Stay on "Added ✓" (disabled) until the shopper changes something —
        // the old 1.6s re-arm invited a second click that doubled the keg, the
        // add-ons, the deposits and the declared empties. See armAgain() below.
        var changed = root.dataset.changedWhileAdding === '1';
        delete root.dataset.changedWhileAdding;
        unlock();
        btn.classList.add('mka-cta--added');
        root.dataset.added = '1';
        announce('Added ✓');
        flyToCart(btn);
        if (emptiesFailed) showMsg('Added — but we couldn\'t save your empty-keg count. Please enter it in the returns box on the cart page.');
        if (changed) armAgain();
      })
      .catch(function (e) {
        delete root.dataset.changedWhileAdding;
        unlock();
        var partialPossible = addStatus === 422;
        if (addStatus) {
          // A rejected add can still put part of it in the cart (Shopify adds the
          // stock it has, e.g. 4 of 6 kegs). Show the real cart in the header and
          // drawer, and have the deposit script reconcile deposits right away.
          readCart().then(function (cart) {
            if (!cart || typeof cart.item_count !== 'number') return;
            document.dispatchEvent(new CustomEvent('cart:update', {
              bubbles: true,
              detail: { sourceId: 'mikes-keg-accessories', data: { source: 'mikes-keg-accessories', sections: {}, itemCount: cart.item_count } }
            }));
          });
          if (typeof window.mikesDepositsResync === 'function') window.mikesDepositsResync();
        }
        showMsg(addStatus
          ? e.message + (partialPossible ? ' Some items may already be in your cart — please check it before adding again.' : '')
          : 'We couldn\'t reach the store. Check your connection and try again.');
        btn.textContent = 'Add to Cart';
        btn.disabled = false;
        console.error('[Keg Accessories]', e);
      });
  });

  // ── v2 UI helpers ───────────────────────────────────────────────
  var statusEl = $('#mka-status');
  function announce(text) { if (statusEl) statusEl.textContent = text; }

  // Re-arm "Add to Cart" after a successful add, on the FIRST real change:
  // anything inside the panel, the keg quantity box, a variant switch, a cart
  // edit made in Horizon's drawer / cart page (their cart:update carries the
  // cart-items section id), or a back/forward-cache restore. The deposit
  // script's own cart:update ('mikes-deposits') and ours are ignored — they
  // arrive right after an add and would bring the double-add back.
  function armAgain() {
    if (root.dataset.syncing === '1') return; // panel re-syncing itself after load/back — not a shopper edit
    if (root.dataset.adding === '1') { root.dataset.changedWhileAdding = '1'; return; }
    if (root.dataset.added !== '1') return;
    delete root.dataset.added;
    var btn = $('#mka-add-to-cart');
    if (!btn || root.dataset.soldOut === 'true') return;
    btn.classList.remove('mka-cta--added');
    btn.textContent = 'Add to Cart';
    btn.disabled = false;
  }
  root.addEventListener('input', armAgain);
  root.addEventListener('change', armAgain);
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (t && (t.name === 'quantity' || (t.closest && t.closest('variant-picker')))) armAgain();
  });
  // Horizon's keg-quantity +/- buttons set the value in code and fire
  // 'quantity-selector:update' (no native 'change'); drawer lines carry a cartLine.
  document.addEventListener('quantity-selector:update', function (e) {
    if (e.detail && e.detail.cartLine) return;
    var t = e.target;
    if (t && t.closest && t.closest('cart-items-component')) return;
    armAgain();
  });
  document.addEventListener('variant:update', armAgain);
  document.addEventListener('cart:update', function (e) {
    var src = e.detail && e.detail.sourceId;
    if (!src || src === 'mikes-keg-accessories' || src === 'mikes-deposits') return;
    var cartSections = Array.prototype.map.call(
      document.querySelectorAll('cart-items-component[data-section-id]'),
      function (el) { return el.getAttribute('data-section-id'); });
    if (cartSections.indexOf(String(src)) !== -1) armAgain();
  });
  window.addEventListener('pageshow', function (e) { if (e.persisted) armAgain(); });

  // Screen-reader status: after a stepper change say "<label> <n>." and the
  // running total, debounced so rapid taps produce one announcement.
  var statusTimer = null;
  root.addEventListener('input', function (e) {
    var t = e.target;
    clearTimeout(statusTimer);
    statusTimer = setTimeout(function () {
      var prefix = '';
      if (t && t.matches && t.matches('.mka-stepper input')) {
        var first = (t.getAttribute('aria-labelledby') || '').split(' ')[0];
        var lbl = first && document.getElementById(first);
        prefix = (lbl ? lbl.textContent.trim() + ' ' : '') + t.value + '. ';
      }
      var tot = $('#mka-addons-total');
      announce(prefix + 'Order total add-ons ' + (tot ? tot.textContent : ''));
    }, 500);
  });

  // Fly-to-cart, mirroring Horizon's product-form.js, only when the theme's
  // animation setting is on, the component exists and motion is allowed.
  function flyToCart(source) {
    try {
      if (root.dataset.atcAnimation !== 'true') return;
      if (!window.customElements || !customElements.get('fly-to-cart')) return;
      if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      var cartIcon = document.querySelector('.header-actions__cart-icon');
      var image = root.dataset.flyImage;
      if (!cartIcon || !image) return;
      var fly = document.createElement('fly-to-cart');
      fly.classList.add('fly-to-cart--main');
      fly.style.setProperty('background-image', 'url(' + image + ')');
      fly.style.setProperty('--start-opacity', '0');
      fly.source = source;
      fly.destination = cartIcon;
      document.body.appendChild(fly);
    } catch (err) { /* decoration only — never affects the add */ }
  }

  // Horizon's floating "Add to cart" bar looks for the theme's own add button,
  // which keg pages replace with this panel — so on keg pages it did nothing.
  // Route its tap to the panel instead (scroll + focus; never auto-add: the bar
  // shows the keg-only price and the shopper hasn't reviewed the setup).
  if (!window.__mkaStickyRoute) {
    window.__mkaStickyRoute = true;
    document.addEventListener('click', function (e) {
      var bar = e.target && e.target.closest && e.target.closest('sticky-add-to-cart .sticky-add-to-cart__button, sticky-add-to-cart [ref="addToCartButton"]');
      if (!bar) return;
      var panel = document.getElementById('mikes-keg-accessories');
      var cta = document.getElementById('mka-add-to-cart');
      if (!panel || !cta) return;
      var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
      try { panel.scrollIntoView({ block: 'end', behavior: reduce ? 'instant' : 'smooth' }); }
      catch (err) { panel.scrollIntoView(false); }
      try { cta.focus({ preventScroll: true }); } catch (err) { cta.focus(); }
    });
  }

  // ── Pinned total row helpers (v2) ───────────────────────────────
  // The total + Add to Cart row is position:sticky at the bottom of the panel.
  // Keep whatever the shopper is working on visible above it.
  function pinRow() { return root.querySelector('.mka-cta-wrap'); }
  // Top edge of the pinned row while it is stuck to the bottom of the screen,
  // or null when it is sitting in its normal place at the end of the panel.
  function pinnedTop() {
    var row = pinRow(); if (!row) return null;
    var r = row.getBoundingClientRect();
    if (Math.abs(r.bottom - window.innerHeight) > 2) return null;
    return r.top;
  }
  function scrollByInstant(dy) {
    try { window.scrollBy({ top: dy, behavior: 'instant' }); } catch (err) { window.scrollBy(0, dy); }
  }
  function keepClear(el) {
    var row = pinRow();
    if (!el || !row || row.contains(el)) return;
    var top = pinnedTop(); if (top === null) return;
    var target = (el.closest && el.closest('.mka-stepper, .mka-tile, .mka-chip, .mka-card, .mka-ro, .mka-swr, .mka-cbx')) || el;
    var overlap = target.getBoundingClientRect().bottom - (top - 8);
    if (overlap > 0) scrollByInstant(overlap);
  }
  // Tell CSS how tall the pinned row is (scroll-margin for focus uses it).
  (function () {
    var row = pinRow();
    if (!row || !window.ResizeObserver) return;
    new ResizeObserver(function () {
      root.style.setProperty('--mka-pin-h', row.offsetHeight + 'px');
    }).observe(row);
  })();
  // Focus moved (Tab) or an arrow key picked another option: make sure it isn't
  // hidden under the pinned row. Safari ignores scroll-margin for on-screen
  // controls and doesn't scroll on arrow-key selection, hence the explicit check.
  root.addEventListener('focusin', function (e) {
    var t = e.target;
    requestAnimationFrame(function () { keepClear(t); });
  });
  root.addEventListener('keydown', function (e) {
    root.classList.add('mka-kbd'); // keyboard in use: show the focus ring (Safari drops :focus-visible after arrow keys)
    var t = e.target;
    if (/^Arrow/.test(e.key) && t && t.type === 'radio') {
      requestAnimationFrame(function () {
        keepClear(root.querySelector('input[name="' + t.name + '"]:checked'));
      });
    }
  });
  root.addEventListener('pointerdown', function () { root.classList.remove('mka-kbd'); });

  // Errors appear inside the pinned row and make it taller; scroll so the row
  // grows into empty space instead of covering the control above it.
  function showMsg(text) {
    var m = $('#mka-msg'), row = pinRow();
    if (!m) return;
    var before = row ? row.offsetHeight : 0;
    m.textContent = text;
    if (!row || !text) return;
    var grew = row.offsetHeight - before;
    if (pinnedTop() !== null) { if (grew > 0) scrollByInstant(grew); return; }
    var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
    try { row.scrollIntoView({ block: 'nearest', behavior: reduce ? 'instant' : 'smooth' }); } catch (err) { /* ignore */ }
  }
  // Clear an old error as soon as the shopper changes something.
  function clearMsg() {
    var m = $('#mka-msg');
    if (m && m.textContent && root.dataset.adding !== '1' && root.dataset.syncing !== '1') m.textContent = '';
  }
  root.addEventListener('input', clearMsg);
  root.addEventListener('change', clearMsg);
  document.addEventListener('quantity-selector:update', function (e) {
    if (!(e.detail && e.detail.cartLine)) clearMsg();
  });

  // Horizon's floating bar can reappear on top of the panel's own pinned row
  // after a fast flick. Hide it while any part of the panel is on screen.
  if ('IntersectionObserver' in window) {
    if (window.__mkaPanelIO) window.__mkaPanelIO.disconnect();
    window.__mkaPanelIO = new IntersectionObserver(function (entries) {
      document.documentElement.classList.toggle('mka-panel-onscreen', entries[entries.length - 1].isIntersecting);
    });
    window.__mkaPanelIO.observe(root);
  }

  function setupLabel(s) {
    if (s === 'kegerator') return 'Kegerator (customer owns)';
    if (s === 'jockey_box') return 'Jockey Box rental';
    if (s === 'keg_tub') return 'Keg Tub rental'; // was 'Keg Tub (free rental)' — the tub is a paid rental (Tom, 2026-09-27)
    return 'No accessories';
  }
})();
